<template>
  <!-- Wrapper de modal con el chrome de epc-modal.scss (dialog / header / body /
       footer). Soporta DOS modos (merge del ModalWrapper de pictogramas):

       1) Declarativo (slots) — uso original:
            <epc-modal-wrapper :active.sync="open" :title="'Título'">
              ...cuerpo...
              <template #footer> ...botones... </template>
            </epc-modal-wrapper>

       2) Imperativo (componente dinámico) — vía ref:
            <epc-modal-wrapper ref="modal" />
            this.$refs.modal.show({
              component: MiComponente,
              title, width, props, listeners,
              overflowVisible, contentClass, scrollBody, canCancel,
            })
          El componente cargado debe emitir 'close' para cerrar (auto-cableado). -->
  <b-modal
    :active="modalActive"
    :width="curWidth"
    scroll="keep"
    :can-cancel="curCanCancel"
    class="epc-modal-shell"
    :custom-content-class="customContentClass"
    @update:active="onActiveChange"
  >
    <div class="epc-modal__dialog" :class="{ 'epc-modal__dialog--scroll': curScrollBody }">
      <div class="epc-modal__header">
        <div class="epc-modal__heading">
          <div class="epc-modal__title">{{ curTitle }}</div>
        </div>
        <button class="epc-modal__close" @click="onClose">
          <b-icon pack="fas" icon="times" size="is-small"></b-icon>
        </button>
      </div>

      <div class="epc-modal__body" :class="{ 'epc-modal__body--scroll': curScrollBody }">
        <!-- Modo imperativo: componente dinámico cargado vía show({ component }). -->
        <component
          v-if="isImperative"
          :is="cfg.component"
          v-bind="cfg.props"
          v-on="cfg.listeners"
          @close="hide"
        />
        <!-- Modo declarativo: contenido por slot. -->
        <slot v-else></slot>
      </div>

      <div v-if="!isImperative && $slots.footer" class="epc-modal__footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'EpcModalWrapper',

  props: {
    active: { type: Boolean, default: false },
    title: { type: String, default: '' },
    width: { type: [Number, String], default: 520 },
    canCancel: { type: Array, default: () => ['escape'] },
    // Si true, el cuerpo scrollea dentro del diálogo (para contenido alto) en vez de
    // desbordarse. Por defecto false (mantiene overflow visible para dropdowns).
    scrollBody: { type: Boolean, default: false },
  },

  data() {
    return {
      // Config del modo imperativo (rellenada por show()).
      imperativeOpen: false,
      cfg: {},
    };
  },

  computed: {
    // Imperativo si show() cargó un componente dinámico.
    isImperative() {
      return !!(this.cfg && this.cfg.component);
    },
    modalActive() {
      return this.isImperative ? this.imperativeOpen : this.active;
    },
    curTitle() {
      return this.isImperative ? (this.cfg.title || '') : this.title;
    },
    curWidth() {
      return this.isImperative ? (this.cfg.width != null ? this.cfg.width : 520) : this.width;
    },
    curCanCancel() {
      return this.isImperative ? (this.cfg.canCancel || ['escape']) : this.canCancel;
    },
    curScrollBody() {
      return this.isImperative ? !!this.cfg.scrollBody : this.scrollBody;
    },
    customContentClass() {
      // Declarativo: overflow visible (comportamiento original) para no recortar dropdowns.
      if (!this.isImperative) {
        return 'epc-modal__content--overflow';
      }
      // Imperativo: overflow visible bajo demanda + clase extra (p. ej. ancho dinámico).
      const classes = [];
      if (this.cfg.overflowVisible) classes.push('epc-modal__content--overflow');
      if (this.cfg.contentClass) classes.push(this.cfg.contentClass);
      return classes.join(' ');
    },
  },

  methods: {
    // API imperativa: carga cualquier componente dentro del modal (como el ModalWrapper
    // de pictogramas). El componente debe emitir 'close' para cerrar.
    show({ component, props = {}, listeners = {}, title = '', width = 520, canCancel = ['escape'], scrollBody = false, overflowVisible = false, contentClass = '' }) {
      this.cfg = { component, props, listeners, title, width, canCancel, scrollBody, overflowVisible, contentClass };
      this.imperativeOpen = true;
    },

    hide() {
      this.imperativeOpen = false;
      this.$nextTick(() => {
        this.cfg = {};
      });
    },

    onActiveChange(value) {
      if (this.isImperative) {
        if (!value) this.hide();
      } else {
        this.$emit('update:active', value);
      }
    },

    onClose() {
      if (this.isImperative) {
        this.hide();
      } else {
        this.$emit('update:active', false);
      }
    },
  },
};
</script>
<!-- Sin <style>: todo el estilo (shell + ajustes del wrapper) vive en
     resources/assets/sass/front/components/epc-modal.scss, importado en front/app.scss. -->
